// Ordered presentation of authoritative commits.
//
// The ClientMirror folds every inbound commit the moment it arrives, so the
// authoritative mirror is always current. What the player SEES lags behind it
// only while a combat strike window is playing on the overlay: every commit
// that lands during the playback (the effect step, a Deathrite's prompt, the
// next window, ordinary play after the fight) waits in this queue and is
// displayed, in version order, once the scene is done. A choice therefore
// never appears over an earlier strike animation, and the board never shows
// a later stage's deaths or life while an earlier stage is still on stage.
//
// This replays committed projections for display. It never resolves rules:
// the state each entry displays is the mirror's state at that version.
//
// A Full supersedes everything queued (a reconnect rehydrates at the latest
// committed stage; nothing is replayed to rebuild it), and an overflowing
// queue is dropped in favour of a fresh Full rather than growing without bound.
export const MAX_QUEUE = 64;

// `applyFolds(msg, state, prevState)` renders one commit and returns true when
// it put a strike playback on stage (the queue then waits for `sceneDone`).
// `instant` (replay playback) never waits: scenes there are position-driven.
export function createCombatPresentation({ applyFolds, requestFull, instant = false } = {}) {
  const queue = [];
  let displayed = null; // { version, state }
  let busy = null; // stageId of the strike playback currently on stage
  let overflowed = false;

  function show(entry) {
    const prev = displayed?.state ?? null;
    displayed = { version: entry.msg?.version ?? -1, state: entry.state };
    const stage = entry.msg?.combat || null;
    const staged = applyFolds(entry.msg, entry.state, prev, stage);
    if (!instant && stage?.kind === 'strike' && staged) busy = stage.stageId;
  }

  function drain() {
    while (!busy && queue.length) show(queue.shift());
  }

  return {
    enqueue(msg, state) {
      if (msg?.kind === 'full') {
        // Rehydrate at the latest committed stage; anything still queued is
        // history the Full already contains.
        queue.length = 0;
        busy = null;
        overflowed = false;
        show({ msg, state });
        return;
      }
      if (queue.length >= MAX_QUEUE) {
        queue.length = 0;
        busy = null;
        overflowed = true;
        requestFull?.();
        return;
      }
      if (overflowed) return; // waiting for the Full that resets us
      queue.push({ msg, state });
      drain();
    },
    // The strike playback finished (or was skipped). Skip drains committed
    // presentation; it can never answer a rules choice.
    sceneDone(stageId) {
      if (busy && stageId && busy !== stageId) return;
      busy = null;
      drain();
    },
    isBehind() { return queue.length > 0 || busy != null; },
    playing() { return busy; },
    displayedState() { return displayed?.state ?? null; },
    displayedVersion() { return displayed?.version ?? -1; },
    // Observability for parity checks and the scene digest: the queue is
    // contiguous when every waiting commit follows the displayed version.
    snapshot() {
      let contiguous = true;
      let v = displayed?.version ?? -1;
      for (const e of queue) {
        if (e.msg?.version !== v + 1) contiguous = false;
        v = e.msg?.version ?? v;
      }
      return { queued: queue.length, busy, displayedVersion: displayed?.version ?? -1, contiguous, overflowed };
    },
  };
}
